import { Fragment, useState } from 'react';
import { flexRender } from '@tanstack/react-table';
import { s } from '../listPageStyles';
import { ColumnFilterPopover } from './ColumnFilterPopover';
import { C360Pivot } from './C360Pivot';

/**
 * c360 liste tablosu: sıralama + filtre başlıkları ve iç içe gruplu gövde.
 *
 * Props:
 *   table                — TanStack table instance
 *   columnFilterMeta     — { columnId: { type, ... } }
 *   columnFilters        — aktif filtreler
 *   onApplyFilter(id,n)  — filtre uygula
 *   onClearFilter(id)    — filtre temizle
 *   resolveFilterProps(columnId, meta) => { options?, fetchOptions?, resolveLabel? }
 *   isGrouping, nestedGroupBuckets, expandedGroups, toggleGroupExpand,
 *   bucketCounts, bucketLoadingByPath
 *   loading, selected, onRowClick, onRowDoubleClick
 *   emptyText
 *   enablePivot          — Tablo/Pivot sekmesini göster (varsayılan: açık)
 *   pivotKey             — pivot düzeninin kalıcılık anahtarı (varsayılan: sayfa yolu)
 *   loadAllRows()        — Promise<rows[]>: pivot açılınca filtreye uyan TÜM veriyi çeker.
 *                          Verilmezse pivot yalnız yüklü sayfayla çalışır ve bunu yazar.
 *                          (useC360List.fetchAllRows bu imzayı karşılar.)
 *
 * PİVOT NEDEN BURADA: 95 c360 listesi var; pivot'u tek tek sayfalara eklemek yerine ortak
 * tabloya koyduk — tüm liste ekranları sabit raporlarla AYNI yeteneklere sahip olsun.
 */
export function C360Table({
  table,
  columnFilterMeta = {},
  columnFilters = {},
  onApplyFilter,
  onClearFilter,
  resolveFilterProps,
  isGrouping = false,
  nestedGroupBuckets = [],
  expandedGroups,
  toggleGroupExpand,
  bucketCounts = {},
  bucketLoadingByPath = {},
  loading = false,
  selected,
  onRowClick,
  onRowDoubleClick,
  emptyText = 'Kayıt bulunamadı',
  enablePivot = true,
  pivotKey,
  loadAllRows,
}) {
  const allColumns = table.getAllLeafColumns();
  const hasRows = table.getRowModel().rows.length > 0;
  const [mode, setMode] = useState('table');
  const resolvedPivotKey = pivotKey || (typeof window !== 'undefined' ? window.location.pathname : 'c360');

  // Gruplama açıkken pivot kapalı: iki farklı kırılım aynı anda anlamsız, gruplu satır modeli
  // pivot'a düz veri veremez.
  const pivotAvailable = enablePivot && !isGrouping;

  if (pivotAvailable && mode === 'pivot') {
    return (
      <div className="c360-table-wrap c360-has-pivot">
        <ModeTabs mode={mode} setMode={setMode} />
        <C360Pivot table={table} pivotKey={resolvedPivotKey} loadAllRows={loadAllRows} />
      </div>
    );
  }

  return (
    <div className="c360-table-wrap">
      {pivotAvailable && <ModeTabs mode={mode} setMode={setMode} />}
      <table className="c360-table">
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                const canSort = header.column.getCanSort();
                const sortState = header.column.getIsSorted();
                const filterType = header.column.columnDef.meta?.filterType;
                const filterMeta = columnFilterMeta[header.column.id];
                const extra = (filterMeta && resolveFilterProps) ? (resolveFilterProps(header.column.id, filterMeta) || {}) : {};
                return (
                  <th
                    key={header.id}
                    className={[canSort ? 'is-sortable' : '', header.column.id === 'actions' ? 'c360-act-th' : ''].filter(Boolean).join(' ') || undefined}
                    onClick={canSort ? header.column.getToggleSortingHandler() : undefined}
                  >
                    {header.isPlaceholder ? null : (
                      <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        {canSort && (
                          <span className="c360-sort-icon">{sortState === 'asc' ? '▲' : sortState === 'desc' ? '▼' : ''}</span>
                        )}
                        {filterType && filterMeta && (
                          <ColumnFilterPopover
                            column={header.column}
                            filter={columnFilters[header.column.id]}
                            filterType={filterMeta.type}
                            options={extra.options ?? filterMeta.options}
                            fetchOptions={extra.fetchOptions}
                            resolveLabel={extra.resolveLabel}
                            onApply={(next) => {
                              if ((filterMeta.type === 'select' || filterMeta.type === 'asyncSelect') && (next.value === '' || next.value == null)) {
                                onClearFilter(header.column.id);
                              } else {
                                onApplyFilter(header.column.id, next);
                              }
                            }}
                            onClear={() => onClearFilter(header.column.id)}
                          />
                        )}
                      </span>
                    )}
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>
        <tbody>
          {(() => {
            const tanstackRows = table.getRowModel().rows;
            if (tanstackRows.length === 0) {
              // Boş gövde: yükleniyorsa metni başlıkların ALTINDA, gövde içinde göster
              // (absolute overlay başlıkların üstüne taşmasın). Bitince "Kayıt bulunamadı".
              return (
                <tr className="is-empty">
                  <td colSpan={allColumns.length}>{loading ? 'Yükleniyor...' : emptyText}</td>
                </tr>
              );
            }
            const visibleColCount = table.getVisibleLeafColumns().length;
            const renderRow = (trow) => {
              const isSelectedRow = selected?.id === trow.original.id;
              return (
                <tr
                  key={trow.id}
                  className={isSelectedRow ? 'is-selected' : undefined}
                  onClick={() => onRowClick?.(trow.original)}
                  onDoubleClick={() => onRowDoubleClick?.(trow.original)}
                >
                  {trow.getVisibleCells().map((cell) => (
                    <td key={cell.id} className={cell.column.id === 'actions' ? 'c360-actcell' : undefined}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              );
            };
            if (!isGrouping) {
              return tanstackRows.map(renderRow);
            }
            const rowById = new Map(tanstackRows.map((r) => [r.original.id, r]));
            const renderNested = (bucketArr, depth) => bucketArr.map((bucket) => {
              const isExpanded = expandedGroups.has(bucket.path);
              const lvl = Math.min(depth + 1, 3);
              const backendCount = bucketCounts[bucket.path];
              const isLoadingBucket = bucketLoadingByPath[bucket.path];
              return (
                <Fragment key={bucket.path}>
                  <tr className={`is-group-header level-${lvl}`} onClick={() => toggleGroupExpand(bucket.path)}>
                    <td colSpan={visibleColCount}>
                      <span style={{ paddingLeft: depth * 20 }}>
                        <span className={`c360-group-chevron${!isExpanded ? ' is-collapsed' : ''}`}>▼</span>
                        <span className="c360-group-label">{bucket.label}</span>
                        <span
                          className="c360-group-count"
                          title={backendCount != null ? `${backendCount} kayıt (backend)` : 'Sayı henüz yüklenmedi'}
                        >
                          {backendCount != null ? backendCount : '—'}
                        </span>
                        {isLoadingBucket && <span style={{ marginLeft: 8, fontSize: 11, color: 'var(--accent)' }}>⏳ yükleniyor…</span>}
                      </span>
                    </td>
                  </tr>
                  {isExpanded && (
                    bucket.children
                      ? renderNested(bucket.children, depth + 1)
                      : bucket.rows.map((raw) => {
                        const trow = rowById.get(raw.id);
                        return trow ? renderRow(trow) : null;
                      })
                  )}
                </Fragment>
              );
            });
            return renderNested(nestedGroupBuckets, 0);
          })()}
        </tbody>
      </table>
      {loading && hasRows && <div style={s.loadingOverlay}>Yükleniyor...</div>}
    </div>
  );
}

/** Tablo ↔ Pivot geçişi — sabit raporlardaki segment düğmesiyle aynı dil. */
function ModeTabs({ mode, setMode }) {
  return (
    <div className="c360-mode-tabs" role="tablist" aria-label="Görünüm">
      {[['table', 'Tablo'], ['pivot', 'Pivot']].map(([value, label]) => (
        <button
          key={value}
          type="button"
          role="tab"
          aria-selected={mode === value}
          className={`c360-mode-tab${mode === value ? ' is-active' : ''}`}
          onClick={() => setMode(value)}
        >
          {label}
        </button>
      ))}
    </div>
  );
}
